/** Boot (06 §1.1): prefs → i18n → auth probe → router. */
import '@fontsource/source-sans-3/latin-400.css';
import '@fontsource/source-sans-3/latin-ext-400.css';
import '@fontsource/source-sans-3/latin-600.css';
import '@fontsource/source-sans-3/latin-ext-600.css';
import { effect, untracked } from '@preact/signals';
import { render } from 'preact';
import { App } from './app/App';
import { serverEnv } from './app/env';
import { initLocale, localeSignal } from './app/locale';
import { logError } from './app/log';
import { registerServiceWorker } from './pwa/register';
import { initAuth } from './stores/auth';
import './styles.css';

async function boot(): Promise<void> {
  try {
    await initLocale();
  } catch (e) {
    // The strings failed to load (offline without a cache, or a stale build): render anyway; keys show as ids.
    logError('i18n', e);
  }
  initAuth();
  const root = document.getElementById('app');
  if (root) {
    // a locale switch re-mounts the tree in the new language, without a reload (06 §14.4): the router hands its
    // route the same vnode, so a plain re-render would not reach the pages. The switcher lives on content pages,
    // so no table state is lost.
    effect(() => {
      const locale = localeSignal.value;
      untracked(() => render(<App key={locale} />, root));
    });
  }
  void registerServiceWorker();
  const q = new URLSearchParams(location.search);
  const wanted = q.get('test') === '1' || import.meta.env['VITE_TEST_HOOK'] === '1';
  // 06 §17.1: loaded only in test mode, so it adds no bytes otherwise; never in production (AC-NFR-05).
  if (wanted && (await serverEnv()) !== 'production') {
    const { installTestHook } = await import('./testing/testHook');
    installTestHook();
  }
}

boot().catch((e: unknown) => logError('boot', e));
